import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { DragDropProvider, DragOverlay, type DragEndEvent, PointerSensor } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { TreeList } from '@ibsys/kit';
import { GripVerticalIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyNbspTypography } from '@/shared/lib';
import { getAllGroupNodeIds, getAncestorGroupIds } from '../../lib';
import type { NavigationNode } from '../../model';
import { SortableTopLevelNode } from './SortableTopLevelNode';

type Props = {
  nodes: NavigationNode[];
  className?: string;
  onNodeSelect?: (nodeId: string) => void;
  selectedNodeId?: string | null;
  /** Новый порядок верхнеуровневых узлов после перетаскивания. */
  onReorder: (nodeIds: string[]) => void;
};

/** Порог в пикселях, после которого нажатие считается перетаскиванием, а не кликом по узлу. */
const DRAG_ACTIVATION_DISTANCE = 5;

export const NavigationTree: FC<Props> = ({ nodes, className, onNodeSelect, selectedNodeId, onReorder }) => {
  const [expanded, setExpanded] = useState<string[]>(() => getAllGroupNodeIds(nodes));
  const hasExpandedDefault = useRef(nodes.length > 0);

  // При первой загрузке непустого дерева раскрываем все группы по умолчанию.
  useEffect(() => {
    if (!hasExpandedDefault.current && nodes.length > 0) {
      setExpanded(getAllGroupNodeIds(nodes));
      hasExpandedDefault.current = true;
    }
  }, [nodes]);

  const expandToNode = useCallback(
    (nodeId: string) => {
      const ancestorIds = getAncestorGroupIds(nodes, nodeId);
      if (ancestorIds.length === 0) return;
      setExpanded((prev) => {
        const prevSet = new Set(prev);
        let added = false;
        for (const id of ancestorIds) {
          if (!prevSet.has(id)) {
            prevSet.add(id);
            added = true;
          }
        }
        // Возвращаем тот же массив, если ничего не добавили — иначе TreeList.Root
        // ререндерится на каждый чих scrollspy с новой ссылкой `value`.
        return added ? Array.from(prevSet) : prev;
      });
    },
    [nodes],
  );

  useEffect(() => {
    if (selectedNodeId && nodes.length > 0) {
      expandToNode(selectedNodeId);
    }
  }, [selectedNodeId, nodes.length, expandToNode]);

  // Путь от корня до активного узла (включая сам активный) — для компаратора `React.memo`.
  const selectedPathSet = useMemo(() => {
    if (!selectedNodeId) return new Set<string>();
    const ancestors = getAncestorGroupIds(nodes, selectedNodeId);
    return new Set<string>([...ancestors, selectedNodeId]);
  }, [selectedNodeId, nodes]);

  const sensors = useMemo(
    () => [
      PointerSensor.configure({
        activationConstraints: [new PointerActivationConstraints.Distance({ value: DRAG_ACTIVATION_DISTANCE })],
      }),
    ],
    [],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return;
    const { source } = event.operation;
    if (!isSortable(source)) return;
    const { initialIndex, index } = source;
    if (initialIndex === index) return;

    const reordered = [...nodes];
    const [moved] = reordered.splice(initialIndex, 1);
    reordered.splice(index, 0, moved);
    onReorder(reordered.map((node) => node.id));
  };

  if (nodes.length === 0) return null;
  return (
    <DragDropProvider sensors={sensors} modifiers={[RestrictToVerticalAxis]} onDragEnd={handleDragEnd}>
      <TreeList.Root
        className={className}
        value={expanded}
        onValueChange={setExpanded}
        activeNodeId={selectedNodeId ?? undefined}
      >
        {nodes.map((node, index) => (
          <SortableTopLevelNode
            key={node.id}
            node={node}
            index={index}
            onNodeSelect={onNodeSelect}
            selectedNodeId={selectedNodeId}
            selectedPathSet={selectedPathSet}
          />
        ))}
      </TreeList.Root>
      <DragOverlay>
        {(source) => {
          const dragged = nodes.find((node) => node.id === String(source.id));
          if (!dragged) return null;
          return (
            <div className="flex items-center gap-2 rounded-lg bg-card px-3 py-2 shadow-lg ring-1 ring-border">
              <GripVerticalIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="text-sm font-bold">{applyNbspTypography(dragged.name)}</span>
            </div>
          );
        }}
      </DragOverlay>
    </DragDropProvider>
  );
};
